import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaPen, FaTable } from 'react-icons/fa';
import { FaCircleXmark } from 'react-icons/fa6';
import {
  useNavigate,
  useParams,
  useRouteError,
  useSearchParams,
} from 'react-router-dom';
import {
  ActionConfirmationModal,
  Badge,
  Button,
  ClientErrors,
  CreateCertificateModal,
  type ErrorMessageProps,
  LinkButton,
  SectionLayout,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import moment from 'moment';

import { useFeatureToggle } from '../../../context/auth/auth-hooks';
import { useAuthenticatedUser } from '../../../context/auth/auth-hooks';
import { usePlatform } from '../../../context/PlatformContext';
import { PoliciesApi } from '../../../generated/api-client';
import { getBffApiConfiguration } from '../../../network/AuthRequests';
import {
  ErrorBoundaryFallback,
  parseError,
} from '../../../router/PlatformErrorBoundary/PlatformErrorBoundary';
import { ActiveOpenCoverTooltip } from '../../components/ActiveOpenCoverTooltip/ActiveOpenCoverTooltip';
import { CertificatePdfOverrideModal } from '../../components/CertificatePdfOverrideModal/CertificatePdfOverrideModal';
import { QuoteCoverageDescriptions } from '../../components/QuoteCoverageDescriptions/QuoteCoverageDescriptions';
import { useCreateCertificatePdfOverride } from '../../hooks/useCerificatePdfOverride';
import { policiesQueries } from '../network/queries';
import { PolicyAdditionalClauses } from './PolicyAdditionalClauses/PolicyAdditionalClauses';
import { PolicyClaims } from './PolicyClaims/PolicyClaims';
import styles from './PolicyDetailsPage.module.scss';
import { PolicyInsuranceDetails } from './PolicyInsuranceDetails/PolicyInsuranceDetails';
import { PolicyPriceBreakdown } from './PolicyPriceBreakdown/PolicyPriceBreakdown';
import { PolicyShipmentDetails } from './PolicyShipmentDetails/PolicyShipmentDetails';
import { PollingFileList } from './PollingFileList/PollingFileList';

export type PolicyRouteParams = {
  policyId: string;
  certificateId: string;
};

export function PolicyDetailsPageErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const policyNotFoundError: ErrorMessageProps = {
    type: 'PageNotFound',
    title: 'Oops!',
    details: ['Sorry, the policy you are looking for does not exist.'],
    actions: [
      {
        variant: 'secondary',
        label: 'Back to Policies',
        leftIcon: <FaTable />,
        onPress: () => {
          navigate('/policies');
        },
      },
    ],
  };

  // Override the default error message for 404 errors
  if (parseError(error).includes(ClientErrors.PageNotFound)) {
    return <ErrorBoundaryFallback {...policyNotFoundError} />;
  }

  return <ErrorBoundaryFallback />;
}

const useCancelPolicy = () => {
  const navigate = useNavigate();
  const { isWtw } = usePlatform();

  const mutation = useMutation({
    mutationFn: async (data: {
      reason: string;
      policyId: number;
      userId: number;
    }) => {
      if (!isWtw) {
        throw new Error('Unsupported platform for cancel policy');
      }
      const apiConfig = await getBffApiConfiguration();
      return new PoliciesApi(apiConfig).cancelPolicy({
        policyId: data.policyId,
        bffWtwFrontendCancelPolicyRequest: {
          cancel_description: data.reason,
          user_id: data.userId,
        },
      });
    },
    onSuccess: () => {
      navigate('/policies?cancelled=true');
    },
  });
  return mutation;
};

export const PolicyDetailsPage = () => {
  const { platform } = usePlatform();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReady = searchParams.get('ready') === 'true';
  const [isModalOpen, setIsModalOpen] = useState(isReady);
  const [isPdfOverideOpen, setIsPdfOverrideOpen] = useState(false);

  const user = useAuthenticatedUser();
  const { isWtw } = usePlatform();

  const enableReferPolicyFlow = user?.settings.find(
    (s) => s.name === 'enable_refer_a_policy_flow',
  )?.value;

  const { policyId } = useParams<PolicyRouteParams>();

  const {
    data: policy,
    isLoading,
    refetch: refetchPolicy,
  } = useSuspenseQuery(policiesQueries.details({ policyId }));
  const certificate_id = policy?.certificate_id;
  const { certificate_status, proof_of_cover_status } = policy ?? {};
  const title = t('details.Title', {
    ns: 'policies',
    context: platform,
    // Both wtw and breeze use the certificate_id to determine the title
    id: certificate_id,
  });
  const isAuthorizedToDuplicatePolicy = policy?.quote?.permissions?.create;
  const isAuthorizedToEditPolicy = isWtw && policy?.permissions?.edit_issued;

  const isAuthorizedToCancelPolicy = policy?.permissions?.cancel && isWtw;

  const isAuthorized = Boolean(
    user?.permissions.policies?.view || user?.permissions.certificates?.view,
  );

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const userId = user?.id;

  const cancelPolicyMutation = useCancelPolicy();
  const [cancelReason, setCancelReason] = useState('');

  const certificateUrlQuery = useQuery({
    queryKey: ['certificateUrl', policyId, policy?.draft_certificate_url],
    queryFn: async () => {
      const draftCertificateUrl = policy?.draft_certificate_url;
      if (!policyId) return null;
      if (!draftCertificateUrl) return null;
      try {
        const response = await fetch(draftCertificateUrl);
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error fetching certificate URL:', error);
        return null;
      }
    },
    enabled: Boolean(policyId),
  });

  const createCertificatePdfOverrides = useCreateCertificatePdfOverride({
    onSuccess: () => {
      refetchPolicy();
      setIsPdfOverrideOpen(false);
      setSearchParams(undefined, {
        replace: true,
      });
    },
  });

  const certificateUrl = certificateUrlQuery.data;

  const isPdfEditingEnabled = useFeatureToggle('enable-pdf-editing');

  return (
    <SectionLayout
      isAuthorized={isAuthorized}
      data-testid="policy-details-page"
      tooltip={
        isWtw
          ? {
              trigger: <ActiveOpenCoverTooltip.Trigger />,
              content: <ActiveOpenCoverTooltip.Content />,
            }
          : undefined
      }
      actions={
        <>
          {isAuthorizedToEditPolicy && (
            <LinkButton
              data-testid="edit-policy-button"
              variant="secondary"
              label={`Update & Reissue ${isWtw ? 'Certificate' : 'Policy'}`}
              leftIcon={<FaPen />}
              width="auto"
              href={`/policies/${policyId}/${certificate_id}/edit`}
            />
          )}
          {isAuthorizedToDuplicatePolicy && (
            <LinkButton
              data-testid="duplicate-policy-button"
              variant="secondary"
              label={t('Duplicate', {
                ns: 'common',
                context: platform,
              })}
              leftIcon={<FaCopy />}
              width="auto"
              href={`/quotes/duplicate/${policy?.quote?.id}`}
            />
          )}
          {isAuthorizedToCancelPolicy && (
            <Button
              data-testid="cancel-policy-button"
              variant="secondary"
              label="Cancel"
              leftIcon={<FaCircleXmark />}
              width="auto"
              onPress={() => {
                setIsCancelModalOpen(true);
              }}
              isDisabled={moment(policy?.created_time).isBefore(
                moment().subtract(30, 'days'),
              )}
            />
          )}
        </>
      }
      title={title}
      backLink="/policies"
      statusBadge={
        !isLoading &&
        !certificate_status &&
        !proof_of_cover_status && (
          <Badge variant="default" data-testid="documents-pending-badge">
            <Typography>
              {t('PendingCargoOwnerApproval', {
                ns: 'common',
                context: platform,
              })}
            </Typography>
          </Badge>
        )
      }
    >
      <div className={styles.policiesDetailsPage}>
        <div className={styles.contentWrapper}>
          <div className={styles.wrapper}>
            <div className={styles.detailsLayout}>
              <div className={styles.policyDetails}>
                <Suspense
                  fallback={
                    <div
                      style={{ height: '330px' }}
                      data-testid="skeleton-loader"
                      className={styles.skeletonLoader}
                    />
                  }
                >
                  <PolicyShipmentDetails />
                </Suspense>

                <Suspense
                  fallback={
                    <div
                      style={{
                        height: '230px',
                      }}
                      data-testid="skeleton-loader"
                      className={styles.skeletonLoader}
                    />
                  }
                >
                  <PolicyInsuranceDetails />
                </Suspense>
              </div>
              <Suspense
                fallback={
                  <div
                    style={{
                      flex: 0.2,
                    }}
                    className={styles.skeletonLoader}
                  />
                }
              >
                <PolicyPriceBreakdown />
              </Suspense>
            </div>

            <Suspense
              fallback={
                <div
                  style={{ height: '200px' }}
                  className={styles.skeletonLoader}
                />
              }
            >
              <PolicyAdditionalClauses />
            </Suspense>

            <Suspense
              fallback={
                <div
                  style={{ height: '100px' }}
                  className={styles.skeletonLoader}
                />
              }
            >
              {policyId && (
                <PollingFileList
                  policyId={policyId}
                  onEditCertificatePdfPress={() => setIsPdfOverrideOpen(true)}
                />
              )}
            </Suspense>

            {/* TODO policyId should always be defined - update suspense query accordingly */}
            <Suspense
              fallback={
                <div
                  style={{ height: '100px' }}
                  className={styles.skeletonLoader}
                />
              }
            >
              <PolicyClaims policyId={policyId} />
            </Suspense>

            <Suspense
              fallback={
                <div
                  style={{ height: '100px' }}
                  className={styles.skeletonLoader}
                />
              }
            >
              <QuoteCoverageDescriptions policyId={policyId} />
            </Suspense>
          </div>
        </div>
      </div>
      <CreateCertificateModal
        showActionButton={true}
        actionButton={
          isPdfEditingEnabled && (
            <Button
              variant="primary"
              size="medium"
              width="full"
              label="Preview & Edit Display Details"
              onPress={() => {
                setIsPdfOverrideOpen(true);
                // TODO fix duplicate state
                setIsModalOpen(false);
                setSearchParams(undefined, {
                  replace: true,
                });
              }}
            />
          )
        }
        data-testid="policy-creation-success-dialog"
        title={
          enableReferPolicyFlow ? 'Thank You!' : 'Your certificate is ready!'
        }
        description={
          enableReferPolicyFlow
            ? 'The policy was successfully referred.'
            : 'You can now view your certificate in the certificates page!'
        }
        isOpen={isModalOpen}
        onOpenChange={(value) => {
          setIsModalOpen(value);
          setSearchParams(undefined, {
            replace: true,
          });
        }}
        onExit={() =>
          setSearchParams(undefined, {
            replace: true,
          })
        }
      />
      <ActionConfirmationModal
        data-testid="cancel-certificate-modal"
        isOpen={isCancelModalOpen}
        onOpenChange={(value) => {
          setIsCancelModalOpen(value);
        }}
        onExit={() => setIsCancelModalOpen(false)}
        onConfirmChanges={() => {
          if (!userId) return;
          cancelPolicyMutation.mutate({
            reason: cancelReason,
            policyId: Number(policyId),
            userId,
          });
          setIsCancelModalOpen(false);
        }}
        confirmButtonDisabled={cancelReason.length < 3}
        heading={
          <div className="flex flex-col gap-2 self-start w-full">
            {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
            <Typography level="h3">
              Cancel Certificate {certificate_id}
            </Typography>
            {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
            <Typography>
              Tell us the reason for cancelling this Certificate{' '}
              <span className="text-red-500 text-sm">*</span>
            </Typography>
            <Typography>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full h-32 border border-gray-300 rounded-md p-2"
                placeholder="Type your reason here..."
              />
            </Typography>
          </div>
        }
      />
      {certificateUrl && policyId && certificate_id && (
        <CertificatePdfOverrideModal
          policyId={policyId}
          certificateId={certificate_id}
          certificateUrl={certificateUrl}
          isPdfOverideOpen={isPdfOverideOpen}
          setIsPdfOverrideOpen={setIsPdfOverrideOpen}
          onSave={(data) => {
            createCertificatePdfOverrides.mutate({
              bffCreateCertificatePdfOverrideRequest: {
                policy_id: parseInt(policyId),
                overrides: data,
              },
            });
          }}
          isSaving={createCertificatePdfOverrides.isPending}
        />
      )}
    </SectionLayout>
  );
};
